import {
    Box,
    List,
    ListItem,
    Checkbox,
    useColorMode,
    Input,
    InputGroup,
    InputLeftElement,
    Text,
    VStack,
    Badge,
    Flex,
    Icon,
    Tooltip,
    Button
} from '@chakra-ui/react';
import { MdSearch, MdInfoOutline } from 'react-icons/md';
import { useState, useMemo } from 'react';
import { useAssistant } from "../../context/AssistantContext";

const MCPSelect = () => {
    const { formState, mcps, setFormState } = useAssistant();
    const { colorMode } = useColorMode();
    const [searchQuery, setSearchQuery] = useState('');

    const filteredTools = useMemo(() => {
        if (!searchQuery) return mcps;
        return mcps.filter(mcp =>
            mcp.name.toLowerCase().includes(searchQuery.toLowerCase())
        );
    }, [mcps, searchQuery]);

    const handleSelectAll = () => {
        setFormState(prevState => ({ ...prevState, mcps: mcps }));
    };

    const handleClearAll = () => {
        setFormState(prevState => ({ ...prevState, mcps: [] }));
    };

    const handleToolSelect = (toolId) => {
        setFormState(prev => {
            const prevTools = Array.isArray(prev.mcps) ? prev.mcps : [];
            const toolExists = prevTools.some(t => t._id === toolId);

            if (toolExists) {
                // Remove the tool if it exists
                return {
                    ...prev,
                    mcps: prevTools.filter(t => t._id !== toolId)
                };
            } else {
                // Add the tool if it doesn't exist
                const toolToAdd = mcps.find(t => t._id === toolId);
                return {
                    ...prev,
                    mcps: [...prevTools, toolToAdd]
                };
            }
        });
    };

    const styles = {
        light: {
            bg: 'white',
            borderColor: 'gray.200',
            textColor: 'gray.700',
            placeholderColor: 'gray.400',
        },
        dark: {
            bg: 'gray.700',
            borderColor: 'gray.600',
            textColor: 'gray.300',
            placeholderColor: 'gray.500',
        },
    };

    return (
        <VStack spacing={4} align="stretch">
            <InputGroup>
                <InputLeftElement pointerEvents="none">
                    <Icon as={MdSearch} color={colorMode === 'light' ? 'gray.400' : 'gray.500'} />
                </InputLeftElement>
                <Input
                    placeholder="Search mcps..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    bg={colorMode === 'light' ? 'white' : 'gray.700'}
                    borderColor={colorMode === 'light' ? 'gray.200' : 'gray.600'}
                    _hover={{
                        borderColor: colorMode === 'light' ? 'gray.300' : 'gray.500'
                    }}
                />
            </InputGroup>

            <Flex justify="space-between" mb={2}>
                <Button size="sm" onClick={() => handleSelectAll()}>Select All</Button>
                <Button size="sm" onClick={() => handleClearAll()}>Clear All</Button>
            </Flex>

            <Box
                maxH="300px"
                overflowY="auto"
                p={2}
                borderColor={colorMode === 'light' ? 'gray.200' : 'gray.600'}
                borderWidth={1}
                borderRadius="md"
                bg={colorMode === 'light' ? 'white' : 'gray.700'}
            >
                {filteredTools.length === 0 ? (
                    <Text
                        color={colorMode === 'light' ? 'gray.500' : 'gray.400'}
                        textAlign="center"
                        py={4}
                    >
                        {searchQuery ? 'No tools match your search' : 'No tools available'}
                    </Text>
                ) : (
                    <List spacing={3}>
                        {filteredTools.map((tool) => (
                            <ListItem key={tool._id}>
                                <Flex align="center" justify="space-between">
                                    <Checkbox
                                        isChecked={formState.mcps?.some((t) => t._id === tool._id)}
                                        onChange={() => handleToolSelect(tool._id)}
                                        color={colorMode === 'light' ? 'gray.800' : 'white'}
                                        maxW="80%"
                                    >
                                        <Text
                                            noOfLines={1}
                                            color={colorMode === 'light' ? 'gray.700' : 'gray.300'}
                                        >
                                            {tool.name}
                                        </Text>
                                    </Checkbox>
                                    <Tooltip >
                                        <Badge
                                            colorScheme={formState.mcps?.some((t) => t._id === tool._id) ? 'green' : 'gray'}
                                            variant="subtle"
                                            fontSize="xs"
                                        >
                                        </Badge>
                                    </Tooltip>
                                </Flex>
                            </ListItem>
                        ))}
                    </List>
                )}
            </Box>

            <Text
                fontSize="sm"
                color={colorMode === 'light' ? 'gray.500' : 'gray.400'}
                textAlign="right"
            >
                {formState.mcps?.length || 0} of {mcps.length} mcps selected
            </Text>
        </VStack>
    );
};

export default MCPSelect;